/**
 * @since 2026-04-27
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React, { useState } from "react";
import { Box } from "@mui/material";
import EChart from "components/Shared/EChart";
import ChartCard from "components/Shared/ChartCard";
import { filterByPeriod } from "components/Shared/chartUtils";

const formatVolume = (v) => {
  if (v == null || isNaN(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
};

const LiquidityVolumePanel = ({ detail }) => {
  const [period, setPeriod] = useState("all");
  if (!detail) return null;

  const {
    series = [],
    rollingWindow = 21,
    overallAvgVolume,
    recentAvgVolume,
  } = detail;

  if (series.length === 0) {
    return <Box sx={{ fontSize: 13, opacity: 0.7 }}>No volume data available.</Box>;
  }

  const filtered = filterByPeriod(series, period);
  const dates = filtered.map((d) => d.date);

  const option = {
    animation: false,
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        if (!params || !params.length) return "";
        const lines = [params[0].axisValue];
        params.forEach((p) => {
          lines.push(`${p.marker} ${p.seriesName}: ${formatVolume(p.value)}`);
        });
        return lines.join("<br/>");
      },
    },
    legend: { top: 0, right: 0 },
    grid: { left: 60, right: 30, top: 30, bottom: 30 },
    xAxis: { type: "category", data: dates },
    yAxis: {
      type: "value",
      name: "Volume",
      axisLabel: { formatter: (v) => formatVolume(v) },
    },
    series: [
      {
        name: "Daily volume",
        type: "bar",
        data: filtered.map((d) => d.volume),
        itemStyle: { color: "rgba(116, 169, 207, 0.45)" },
        barMaxWidth: 4,
      },
      {
        name: `${rollingWindow}-trading-day avg`,
        type: "line",
        data: filtered.map((d) => d.rollingAvg),
        smooth: true,
        symbol: "none",
        lineStyle: { color: "#fac858", width: 2 },
        z: 5,
      },
    ],
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 4, fontSize: 13 }}>
        <Box>
          <Box sx={{ opacity: 0.7 }}>Recent {rollingWindow}-day avg</Box>
          <Box sx={{ fontSize: 18, fontWeight: 600 }}>{formatVolume(recentAvgVolume)}</Box>
        </Box>
        <Box>
          <Box sx={{ opacity: 0.7 }}>Overall avg</Box>
          <Box sx={{ fontSize: 18, fontWeight: 600 }}>{formatVolume(overallAvgVolume)}</Box>
        </Box>
        <Box>
          <Box sx={{ opacity: 0.7 }}>Day count</Box>
          <Box sx={{ fontSize: 18, fontWeight: 600 }}>{series.length.toLocaleString()}</Box>
        </Box>
      </Box>
      <ChartCard
        title={`Daily volume & ${rollingWindow}-trading-day rolling average`}
        period={period}
        onPeriodChange={setPeriod}
      >
        <EChart option={option} height={300} />
      </ChartCard>
    </Box>
  );
};

export default LiquidityVolumePanel;
